import { c as _c } from "react/compiler-runtime";
import { addHours, startOfHour, subDays } from 'date-fns';
import { useState } from 'react';
import { useHistory } from 'react-router';
import { useTheme } from 'styled-components';
import { SeekPagination } from '../../../../../../../typings/API.interface';
import { OutpostTenant } from '../../../../../../../typings/outpost';
import { formatNumberShorthand } from '../../../../utils';
import Badge from '../../../common/base/Badge';
import Text from '../../../common/base/Text';
import CopyableText from '../../../common/CopyableText';
import { buildPreviewBuckets, GROUP_METRICS_BY_2H, GROUP_METRICS_BY_4H, PreviewMetricEntry } from '../../../common/Histogram/PreviewHistogram';
import DisplayDate from '../../../common/DisplayDate';
import EmptyState, { StyledEmptyStateWrapper } from '../../../common/EmptyState';
import { Div } from '../../../common/helpers/StyledUtils';
import Table from '../../../common/Table/Table';
import useOutpostMetricsQuery from '../../../hooks/useOutpostMetricsQuery';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { TopicsList } from '../Topics';
import { getOutpostTenantDisplayName } from '../utils/tenantDisplay';
import TenantMetricsPreview from './TenantMetricsPreview';
import { TenantsListQueryParams } from './TenantsView';
const now = addHours(startOfHour(new Date()), 1);
const TenantIdentity = t0 => {
  const $ = _c(13);
  const {
    tenant
  } = t0;
  let t1;
  if ($[0] !== tenant) {
    t1 = getOutpostTenantDisplayName(tenant);
    $[0] = tenant;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const display_name = t1;
  if (!display_name) {
    let t2;
    if ($[2] !== tenant.id) {
      t2 = <Div flex={true} min_w={0}><CopyableText value={tenant.id} size="s" mono={true} icon_on_hover={true} /></Div>;
      $[2] = tenant.id;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      align: "center",
      gap: 1
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== display_name) {
    t3 = <Text mono={true} size="s" semi={true} ellipsis={true} min_w={0}>{display_name}</Text>;
    $[5] = display_name;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      flexShrink: 0
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== tenant.id) {
    t5 = <CopyableText value={tenant.id} size="s" mono={true} muted={true} icon_on_hover={true} style={t4}>({tenant.id})</CopyableText>;
    $[8] = tenant.id;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <Div flex={t2} min_w={0}>{t3}{t5}</Div>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
const TenantDestinations = t0 => {
  const $ = _c(3);
  const {
    count
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      align: "center",
      justify: "center"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== count) {
    t2 = <Div flex={t1} min_w={0} w={100}><Badge neutral={true} subtle={true}>{count}</Badge></Div>;
    $[1] = count;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const TenantsList = t0 => {
  const $ = _c(56);
  const {
    tenants,
    pagination,
    loading,
    total,
    filtering
  } = t0;
  const {
    updateSearchQuery
  } = useSearchQuery();
  const [current_page, setCurrentPage] = useState(1);
  const history = useHistory();
  const theme = useTheme();
  let t1;
  bb0: {
    if (!tenants || tenants.length === 0) {
      t1 = null;
      break bb0;
    }
    let t2;
    let t3;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = ["count"];
      t3 = ["tenant_id"];
      $[0] = t2;
      $[1] = t3;
    } else {
      t2 = $[0];
      t3 = $[1];
    }
    let t4;
    let t5;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = subDays(now, 1).toISOString();
      t5 = now.toISOString();
      $[2] = t4;
      $[3] = t5;
    } else {
      t4 = $[2];
      t5 = $[3];
    }
    let t6;
    if ($[4] !== t4) {
      t6 = {
        start: t4,
        end: t5
      };
      $[4] = t4;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    let t7;
    if ($[6] !== tenants) {
      t7 = tenants.map(_temp);
      $[6] = tenants;
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    let t8;
    if ($[8] !== t7) {
      t8 = {
        tenant_id: t7
      };
      $[8] = t7;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    let t9;
    if ($[10] !== t6 || $[11] !== t8) {
      t9 = {
        endpoint: "events" as const,
        measures: t2,
        dimensions: t3,
        granularity: "1h",
        time: t6,
        filters: t8
      };
      $[10] = t6;
      $[11] = t8;
      $[12] = t9;
    } else {
      t9 = $[12];
    }
    t1 = t9;
  }
  const metrics_query = t1;
  const {
    raw_data
  } = useOutpostMetricsQuery(metrics_query);
  let t2;
  let timeseries_2h_by_tenant_id;
  if ($[13] !== raw_data || $[14] !== tenants) {
    const entries = raw_data?.map(_temp2) ?? null;
    const tenant_ids = tenants.map(_temp3);
    timeseries_2h_by_tenant_id = entries && buildPreviewBuckets(tenant_ids, entries, GROUP_METRICS_BY_2H, now);
    t2 = entries && buildPreviewBuckets(tenant_ids, entries, GROUP_METRICS_BY_4H, now);
    $[13] = raw_data;
    $[14] = tenants;
    $[15] = t2;
    $[16] = timeseries_2h_by_tenant_id;
  } else {
    t2 = $[15];
    timeseries_2h_by_tenant_id = $[16];
  }
  const timeseries_4h_by_tenant_id = t2;
  let headers;
  let t3;
  let widths;
  if ($[17] !== loading || $[18] !== raw_data || $[19] !== tenants || $[20] !== timeseries_2h_by_tenant_id || $[21] !== timeseries_4h_by_tenant_id) {
    let t4;
    if ($[25] !== raw_data) {
      t4 = (obj, t5) => {
        const {
          id: id_1
        } = t5;
        obj[id_1] = raw_data?.reduce((acc, entry) => {
          if (entry?.["Events.tenant_id"] === id_1) {
            acc = acc + parseInt(entry?.["Events.count"] || "0", 10);
          }
          return acc;
        }, 0);
        return obj;
      };
      $[25] = raw_data;
      $[26] = t4;
    } else {
      t4 = $[26];
    }
    const count_by_tenant_id = tenants.reduce(t4, {} as Record<string, number | undefined>);
    let t5;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = ["Tenant", "Destinations", "Topics", "Events", "Created At"];
      $[27] = t5;
    } else {
      t5 = $[27];
    }
    headers = t5;
    let t6;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = [{
        min: 200,
        max: 400
      }, {
        min: 120,
        max: 120
      }, {
        min: 200,
        max: 320
      }, {
        min: 120,
        max: 120
      }, {
        min: 152,
        max: 152
      }];
      $[28] = t6;
    } else {
      t6 = $[28];
    }
    widths = t6;
    t3 = loading ? [] : tenants.map(tenant => ({
      id: tenant.id,
      fields: [<TenantIdentity key={`${tenant.id}-id`} tenant={tenant} />, <TenantDestinations key={`${tenant.id}-destinations`} count={tenant.destinations_count} />, <TopicsList key={`${tenant.id}-topics`} topics={tenant.topics} />, <Div key={`${tenant.id}-events`} flex={{
        align: "center",
        gap: 2
      }} min_w={0}><Text w={{
          px: 40
        }} muted={true} number={true}>{formatNumberShorthand(count_by_tenant_id?.[tenant.id] || 0)}</Text><TenantMetricsPreview data_2h_sample={timeseries_2h_by_tenant_id?.[tenant.id] || []} data_4h_sample={timeseries_4h_by_tenant_id?.[tenant.id] || []} tenant={tenant} /></Div>, <DisplayDate key={`${tenant.id}-created`} date={tenant.created_at} show_timezone={false} />]
    }));
    $[17] = loading;
    $[18] = raw_data;
    $[19] = tenants;
    $[20] = timeseries_2h_by_tenant_id;
    $[21] = timeseries_4h_by_tenant_id;
    $[22] = headers;
    $[23] = t3;
    $[24] = widths;
  } else {
    headers = $[22];
    t3 = $[23];
    widths = $[24];
  }
  const rows = t3;
  let t4;
  if ($[29] !== history) {
    t4 = id_2 => {
      history.push(`/tenants/${id_2}`);
    };
    $[29] = history;
    $[30] = t4;
  } else {
    t4 = $[30];
  }
  const onRowSelected = t4;
  if (tenants.length === 0 && !loading && !filtering) {
    const t5 = `tenants-${theme.mode}.png`;
    let t6;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        label: "Get started",
        icon: "grid_view",
        to: "/get-started"
      };
      $[31] = t6;
    } else {
      t6 = $[31];
    }
    let t7;
    if ($[32] !== t5) {
      t7 = <StyledEmptyStateWrapper><EmptyState title="Create your first tenant" description="A tenant is an entity in your product that you publish events to, typically an org, team, or user in your system. Outpost routes those events to the tenant's destinations, which are the webhook endpoints or services where events are delivered." asset={t5} asset_position="background" cta={t6} /></StyledEmptyStateWrapper>;
      $[32] = t5;
      $[33] = t7;
    } else {
      t7 = $[33];
    }
    return t7;
  }
  const t5 = rows as any;
  const t6 = pagination?.limit || 50;
  let t7;
  if ($[34] !== current_page || $[35] !== t6) {
    t7 = {
      current_page,
      page_size: t6,
      entity_name: "tenants"
    };
    $[34] = current_page;
    $[35] = t6;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== current_page || $[38] !== pagination || $[39] !== updateSearchQuery) {
    t8 = pagination?.next ? () => {
      updateSearchQuery({
        next: pagination.next,
        prev: undefined
      });
      setCurrentPage(current_page + 1);
    } : undefined;
    $[37] = current_page;
    $[38] = pagination;
    $[39] = updateSearchQuery;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== current_page || $[42] !== pagination || $[43] !== updateSearchQuery) {
    t9 = pagination?.prev ? () => {
      updateSearchQuery({
        prev: pagination.prev,
        next: undefined
      });
      setCurrentPage(current_page - 1);
    } : undefined;
    $[41] = current_page;
    $[42] = pagination;
    $[43] = updateSearchQuery;
    $[44] = t9;
  } else {
    t9 = $[44];
  }
  let t10;
  if ($[45] !== headers || $[46] !== loading || $[47] !== onRowSelected || $[48] !== t5 || $[49] !== t7 || $[50] !== t8 || $[51] !== t9 || $[52] !== tenants.length || $[53] !== total || $[54] !== widths) {
    t10 = <Div h={100}><Table fill={true} list={true} headers={headers} widths={widths} top_offset={106} rows={t5} loading={loading} loading_title="Tenants loading" loading_message="It may take a few moments to load your tenants." onRowSelected={onRowSelected} pagination={t7} onNextPage={t8} onPreviousPage={t9} current_count={tenants.length} total_count={total} /></Div>;
    $[45] = headers;
    $[46] = loading;
    $[47] = onRowSelected;
    $[48] = t5;
    $[49] = t7;
    $[50] = t8;
    $[51] = t9;
    $[52] = tenants.length;
    $[53] = total;
    $[54] = widths;
    $[55] = t10;
  } else {
    t10 = $[55];
  }
  return t10;
};
export default TenantsList;
function _temp(t0) {
  const {
    id
  } = t0;
  return id;
}
function _temp2(e) {
  return {
    time: new Date(e["Events.createdAt"]).getTime(),
    count: parseInt(e["Events.count"] || "0", 10),
    id: e["Events.tenant_id"],
    dimension: "events"
  };
}
function _temp3(t0) {
  const {
    id: id_0
  } = t0;
  return id_0;
}